import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { faChevronUp } from "@fortawesome/free-solid-svg-icons";
import CountUp from "react-countup";

const Section6 = ({ url }) => {

  const [counterOn, setCounterOn] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCounterOn(true);
        }
      },
      { threshold: 0.4 } // triggers when ~40% visible
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);
  
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (idx) => setOpenIndex(openIndex === idx ? null : idx);

  const faqs = [
    {
      question:
        "We tried AI before, and it looked great in the demo, but never worked in production.",
      answer:
        "This is the most common AI project failure pattern, and it almost always has the same root cause: the solution was built to demonstrate AI capability, not to solve a specific operational problem. Our process starts with problem definition before we touch a model or write a prompt. For novel or complex implementations, we build a contained proof of concept to validate the approach on your actual data before committing to full development.",
    },
    {
      question:
        "We are worried about our data security and confidentiality if we use third-party AI APIs.",
      answer:
        "This is a legitimate concern and one we address at the architecture stage, not as an afterthought. Depending on your requirements, we implement solutions including private API endpoints, on-premise model deployment, data anonymization pipelines, and strict access control. We can also work with open-source models deployed entirely within your infrastructure if data residency is non-negotiable.",
    },
    {
      question:
        "We do not have clean or structured enough data to use AI effectively.",
      answer:
        "Most businesses say this and most are wrong about what it means. You do not need perfect data to start. You need sufficient data and the right approach for your data's current state. We assess data readiness as part of the feasibility stage and, where needed, include data preparation and structuring as part of the project scope.",
    },
    {
      question:
        "How do we know this will not just create more complexity and maintenance overhead?",
      answer:
        "Poorly scoped AI projects do create overhead. Well-scoped ones reduce it. The difference is in how the project is defined. We document everything, build with maintainability as a first-class requirement, and deploy only when your team can operate the system independently. We also offer structured support packages because AI systems need ongoing attention as your data and operations change.",
    },
  ];

  const stats = [
    { end: 20000, suffix: "+", label: "Hours Automated Across Clients" },
    { end: 10, suffix: "+", label: "AI Systems in Production" },
    { end: 80, suffix: "%", label: "Avg. Error Rate Reduction" },
    { end: 90, suffix: "%", label: "Client Satisfaction Rate" },
  ];

  return (
    <section className="home-section-6">
      <div className="container overflow-hidden">

        {/* ── FAQ ── */}
        <h2 className="s6-title" data-aos="fade-up">
          What We Hear Before Every AI Discovery
        </h2>
        <p className="s6-subtitle" data-aos="fade-up" data-aos-delay="100">
          AI is a high-skepticism category right now. Rightfully so. These are the real objections
          that come up most often, and the honest answers behind them.
        </p>

        <div className="s6-faq-list" data-aos="fade-up" data-aos-delay="200">
          {faqs.map((faq, idx) => (
            <div
            className={`s6-faq-item ${openIndex === idx ? "s6-faq-item--open" : ""}`}
            key={idx}
            onClick={() => toggle(idx)}
            >
            <div className="s6-faq-row">
                <span className="s6-faq-question">{faq.question}</span>
               <span className="s6-faq-icon">{openIndex === idx ?  <FontAwesomeIcon icon={faChevronUp} /> :  <FontAwesomeIcon icon={faChevronDown} />}</span>
            </div>
            <div className="s6-faq-answer">
                {faq.answer}
            </div>
            </div>
          ))}
        </div>

        {/* ── STATS ── */}
        <h2 className="s6-stats-title" data-aos="fade-up" data-aos-delay="100" ref={sectionRef}>
          Businesses That Have Stopped Doing <br/> Manually What AI Can Do Better
        </h2>

        <div className="s6-stats-grid" data-aos="fade-up" data-aos-delay="200">
          {stats.map((stat, idx) => (
            <div className="s6-stat-item" key={idx}>
              {/* <span className="s6-stat-number overflow-hidden">{stat.number}</span>
              <span className="s6-stat-label">
                {stat.label.split("\n").map((line, i) => (
                  <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
                ))}
              </span> */}
              <span className="s6-stat-number overflow-hidden">
                {counterOn && (
                  <CountUp
                    start={0}
                    end={stat.end}
                    duration={2}
                    separator=","
                  />
                )}
                {stat.suffix}
              </span>

              <span className="s6-stat-label">
                {stat.label}
              </span>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Section6;